import { Alert, Box, Container, Paper, Skeleton, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useInsightsDashboard } from '../../contexts/InsightsDashboardContext';
import { PERMISSIONS } from '../../shared/constants/permissions';
import AdvancedInsights from './components/AdvancedInsights';
import FilterBar from './components/FilterBar';
import ProposalMetrics from './components/ProposalMetrics';
import ValueMetrics from './components/ValueMetrics';
import { useFetchInsights } from './useFetchInsights';

const EMPTY_ARRAY: never[] = [];

const InsightsDashboard = () => {
  const { state } = useInsightsDashboard();
  const { data, loading, error } = useFetchInsights(state);
  const { hasPermission } = useAccount();

  if (!hasPermission(PERMISSIONS.INSIGHTS_VIEW)) {
    return <Navigate to="/dashboard" replace />;
  }

  const SOURCE_LABELS: Record<string, string> = {
    VP: 'VendorPanel',
    ATM: 'AusTender',
    BNSW: 'Buy NSW',
    TVIC: 'Tenders VIC',
    TSA: 'Tenders SA',
    TWA: 'Tenders WA',
    QT: 'Queensland Tenders',
    manual: 'Manual',
    uploaded: 'Uploaded',
  };

  // Use filterOptions from the backend if available
  const availableOptions = {
    teams: data?.filterOptions?.teams || EMPTY_ARRAY,
    sectors: data?.filterOptions?.sectors || EMPTY_ARRAY,
    clients: data?.filterOptions?.clients || EMPTY_ARRAY,
    sources: data?.filterOptions?.sources || EMPTY_ARRAY,
    states: data?.filterOptions?.states || EMPTY_ARRAY,
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Strategic Insights Dashboard (preview)
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
        {/* Filter Bar */}
        <Box sx={{ gridColumn: 'span 12' }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Filters
            </Typography>
            <FilterBar availableOptions={availableOptions} sourceLabels={SOURCE_LABELS} />
          </Paper>
        </Box>

        {error && (
          <Box sx={{ gridColumn: 'span 12' }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Value Metrics */}
        <Box sx={{ gridColumn: 'span 12' }}>
          <Paper sx={{ p: 2, minHeight: 300, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Value Metrics
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={500} />
            ) : (
              <ValueMetrics data={data} sourceLabels={SOURCE_LABELS} />
            )}
          </Paper>
        </Box>

        {/* Proposal Metrics */}
        <Box sx={{ gridColumn: 'span 12' }}>
          <Paper sx={{ p: 2, minHeight: 300, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Proposal Metrics
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={500} />
            ) : (
              <ProposalMetrics data={data} sourceLabels={SOURCE_LABELS} />
            )}
          </Paper>
        </Box>

        {/* Advanced Performance Insights */}
        <Box sx={{ gridColumn: 'span 12' }}>
          <Paper sx={{ p: 2, minHeight: 300, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Advanced Performance Insights
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <AdvancedInsights data={data} />
            )}
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default InsightsDashboard;
